<script setup lang="ts">
import { computed } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import type { Badge } from '@/types/gamification'

const props = defineProps<{
  visible: boolean
  badge: Badge | null
}>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
  close: []
}>()

const isVisible = computed({
  get: () => props.visible,
  set: (val: boolean) => emit('update:visible', val),
})

// Badge tier colors and points
const tierColors: Record<string, string> = {
  bronze: 'from-amber-600 to-amber-800',
  silver: 'from-gray-300 to-gray-500',
  gold: 'from-yellow-400 to-yellow-600',
  platinum: 'from-cyan-300 to-cyan-500',
}

const tierPoints: Record<string, number> = {
  bronze: 50,
  silver: 100,
  gold: 200,
  platinum: 500,
}

const badgePoints = computed(() => {
  if (!props.badge) return 0
  return tierPoints[props.badge.tier] || 50
})

// Category icons
const categoryIcons: Record<string, string> = {
  glucose: 'pi-chart-line',
  nutrition: 'pi-apple',
  medication: 'pi-heart',
  activity: 'pi-bolt',
  education: 'pi-book',
  community: 'pi-users',
  streak: 'pi-star',
}

function handleClose() {
  isVisible.value = false
  emit('close')
}
</script>

<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :closable="true"
    :showHeader="false"
    class="badge-unlock-dialog"
    :style="{ width: '360px' }"
  >
    <div
      v-if="badge"
      class="text-center py-6 px-4"
    >
      <!-- Confetti effect (CSS animation) -->
      <div class="confetti-container">
        <div v-for="i in 12" :key="i" :class="`confetti confetti-${i}`" />
      </div>

      <!-- Badge icon with glow effect -->
      <div class="relative inline-block mb-6">
        <div
          :class="[
            'w-28 h-28 rounded-full flex items-center justify-center',
            'bg-gradient-to-br',
            tierColors[badge.tier] || tierColors.bronze,
            'shadow-2xl animate-pulse-glow'
          ]"
        >
          <i
            :class="[
              'pi',
              categoryIcons[badge.category] || 'pi-star',
              'text-5xl text-white drop-shadow-lg'
            ]"
          />
        </div>
        <!-- Sparkles -->
        <div class="absolute -top-2 -right-2 text-2xl animate-bounce">
          ✨
        </div>
        <div class="absolute -bottom-1 -left-2 text-xl animate-bounce delay-100">
          ⭐
        </div>
      </div>

      <!-- Badge earned text -->
      <p class="text-sm font-medium text-yellow-600 dark:text-yellow-400 mb-2 uppercase tracking-wider">
        Badge Débloqué !
      </p>

      <!-- Badge name -->
      <h2 class="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        {{ badge.nameFr || badge.name }}
      </h2>

      <!-- Badge description -->
      <p class="text-gray-600 dark:text-gray-400 mb-4">
        {{ badge.descriptionFr || badge.description }}
      </p>

      <!-- Points earned -->
      <div class="inline-flex items-center gap-2 px-4 py-2 bg-green-100 dark:bg-green-900/30 rounded-full mb-6">
        <i class="pi pi-plus text-green-600 dark:text-green-400 text-sm" />
        <span class="font-bold text-green-700 dark:text-green-300">
          {{ badgePoints }} points
        </span>
      </div>

      <!-- Close button -->
      <div>
        <Button
          label="Génial !"
          icon="pi pi-check"
          severity="success"
          class="w-full"
          @click="handleClose"
        />
      </div>
    </div>
  </Dialog>
</template>

<style scoped>
.badge-unlock-dialog :deep(.p-dialog-content) {
  border-radius: 1rem;
  overflow: visible;
}

@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 0 20px 10px rgba(251, 191, 36, 0.3);
  }
  50% {
    box-shadow: 0 0 40px 20px rgba(251, 191, 36, 0.5);
  }
}

.animate-pulse-glow {
  animation: pulse-glow 2s ease-in-out infinite;
}

/* Confetti animation */
.confetti-container {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
}

.confetti {
  position: absolute;
  width: 10px;
  height: 10px;
  opacity: 0;
  animation: confetti-fall 3s ease-in-out forwards;
}

.confetti-1 { left: 10%; animation-delay: 0s; background: #f59e0b; border-radius: 50%; }
.confetti-2 { left: 20%; animation-delay: 0.1s; background: #10b981; }
.confetti-3 { left: 30%; animation-delay: 0.2s; background: #3b82f6; border-radius: 50%; }
.confetti-4 { left: 40%; animation-delay: 0.15s; background: #ec4899; }
.confetti-5 { left: 50%; animation-delay: 0.25s; background: #8b5cf6; border-radius: 50%; }
.confetti-6 { left: 60%; animation-delay: 0.1s; background: #f59e0b; }
.confetti-7 { left: 70%; animation-delay: 0.2s; background: #10b981; border-radius: 50%; }
.confetti-8 { left: 80%; animation-delay: 0.15s; background: #3b82f6; }
.confetti-9 { left: 90%; animation-delay: 0.25s; background: #ec4899; border-radius: 50%; }
.confetti-10 { left: 15%; animation-delay: 0.3s; background: #8b5cf6; }
.confetti-11 { left: 45%; animation-delay: 0.35s; background: #f59e0b; border-radius: 50%; }
.confetti-12 { left: 75%; animation-delay: 0.4s; background: #10b981; }

@keyframes confetti-fall {
  0% {
    opacity: 1;
    top: -10px;
    transform: rotate(0deg) translateX(0);
  }
  100% {
    opacity: 0;
    top: 100%;
    transform: rotate(720deg) translateX(50px);
  }
}

.delay-100 {
  animation-delay: 0.1s;
}
</style>
